import { useId } from "react";
import { ChevronRight, Dumbbell, Leaf, Pill, Utensils } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { dayjs } from "../lib/time";

const COPY = {
  de: {
    today: "Heute", training: "Training", meals: "Mahlzeiten", supplements: "Supplements", recovery: "Recovery",
    empty: "Heute sind keine Maßnahmen geplant.", nothingPlanned: "Heute nichts geplant",
    unavailable: "Tagesdaten noch nicht verfügbar.", countUnavailable: "Anzahl nicht verfügbar",
    statusUnavailable: "Status nicht vollständig verfügbar", open: (label) => `${label} öffnen`,
    unconfirmedTraining: "Teilnahme noch nicht bestätigt",
    units: (n) => `${n} ${n === 1 ? "Einheit" : "Einheiten"}`,
    measures: (n) => `${n} ${n === 1 ? "Maßnahme" : "Maßnahmen"}`,
    windows: (n) => `${n} Einnahmefenster`, planned: (n) => `${n} geplant`,
    completed: (n) => `${n} abgeschlossen`, full: (n) => `${n} vollständig absolviert`,
    confirmed: (n) => `${n} bestätigt`, assumed: (n) => `${n} automatisch angenommen`,
    taken: (n) => `${n} genommen`, pending: (n) => `${n} offen`,
    feedback: (n) => `${n} ${n === 1 ? "Feedback" : "Feedbacks"} offen`,
    stopped30: (n) => `${n}× Abbruch 30 Min.`,
    stopped60: (n) => `${n}× Abbruch 60 Min.`,
    injury: (n) => `${n} mit Beschwerden`,
    missed: (n) => `${n} verpasst`, missedTraining: (n) => `${n} ausgefallen`,
    missedMeal: (n) => `${n} nicht gegessen`,
  },
  en: {
    today: "Today", training: "Training", meals: "Meals", supplements: "Supplements", recovery: "Recovery",
    empty: "No activities planned today.", nothingPlanned: "Nothing planned today",
    unavailable: "Today's data is not available yet.", countUnavailable: "Count unavailable",
    statusUnavailable: "Some status information is unavailable", open: (label) => `Open ${label}`,
    unconfirmedTraining: "Participation not yet confirmed",
    units: (n) => `${n} ${n === 1 ? "session" : "sessions"}`,
    measures: (n) => `${n} ${n === 1 ? "activity" : "activities"}`,
    windows: (n) => `${n} intake ${n === 1 ? "window" : "windows"}`, planned: (n) => `${n} planned`,
    completed: (n) => `${n} completed`, full: (n) => `${n} completed in full`,
    confirmed: (n) => `${n} confirmed`, assumed: (n) => `${n} automatically assumed`,
    taken: (n) => `${n} taken`, pending: (n) => `${n} open`,
    feedback: (n) => `${n} ${n === 1 ? "feedback response" : "feedback responses"} pending`,
    stopped30: (n) => `${n} stopped after 30 min`, stopped60: (n) => `${n} stopped after 60 min`,
    injury: (n) => `${n} with reported symptoms`,
    missed: (n) => `${n} missed`, missedTraining: (n) => `${n} missed`,
    missedMeal: (n) => `${n} not eaten`,
  },
};

const CATEGORIES = [
  { key: "training", Icon: Dumbbell, totalKey: "relevant_count", totalLabel: "units" },
  { key: "meals", Icon: Utensils, totalKey: "planned_count", totalLabel: "planned" },
  { key: "supplements", Icon: Pill, totalKey: "relevant_window_count", totalLabel: "windows" },
  { key: "recovery", Icon: Leaf, totalKey: "relevant_count", totalLabel: "measures" },
];
const TRAINING_SIGNALS = ["injury_count", "stopped_30_count", "stopped_60_count", "missed_count", "feedback_pending_count"];
const STATUS_PRIORITY = {
  missed: 0, open: 1, planned: 1, upcoming: 1, pending_decision: 1,
  assumed: 2, assumed_completed: 2, completed_auto: 2, taken: 3, completed: 3,
};

// Missing counts are deliberately not coerced to zero or reconstructed from IDs.
const count = (data, key) => Number.isInteger(data?.[key]) && data[key] >= 0 ? data[key] : null;
const list = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
const positive = (data, key) => count(data, key) > 0;

function preferredItem(items, training = false) {
  const rank = (item) => {
    if (training && item.injury_id) return -3;
    if (training && ["stopped_30", "stopped_60"].includes(item.completion_outcome)) return -2;
    if (training && item.feedback_pending) return -1;
    return STATUS_PRIORITY[item.status] ?? 1;
  };
  // The backend already orders items by time; equal priorities keep that order.
  return list(items).filter((item) => item.status !== "cancelled" || (training && item.injury_id))
    .reduce((best, item) => !best || rank(item) < rank(best) ? item : best, null);
}

function windowTime(window, timezone) {
  if (window?.at_iso && timezone && dayjs(window.at_iso).isValid()) {
    try { return dayjs(window.at_iso).tz(timezone).format("HH:mm"); } catch { /* Use the projected local time. */ }
  }
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(window?.at || "") ? window.at : "";
}

function rowFor(category, data, copy, date, timezone) {
  const { key, totalKey, totalLabel } = category;
  const total = count(data, totalKey);
  const summary = [total === null ? (data ? copy.countUnavailable : copy.unavailable)
    : total === 0 ? copy.nothingPlanned : copy[totalLabel](total)];
  const details = [];
  const context = [];
  let fields;
  let completedKey;
  let tone = "neutral";
  const add = (into, field, label) => {
    if (positive(data, field)) into.push(copy[label](data[field]));
  };
  let item = preferredItem(data?.items, key === "training");

  if (key === "training") {
    fields = ["completed_count", "planned_count", "full_count", ...TRAINING_SIGNALS];
    completedKey = "full_count";
    // Completed schedule rows include aborted sessions and outstanding feedback.
    // In those cases only the explicit full outcome establishes full participation.
    const hasSignals = TRAINING_SIGNALS.some((field) => positive(data, field));
    add(summary, "injury_count", "injury");
    add(summary, "full_count", hasSignals ? "full" : "completed");
    add(details, "stopped_30_count", "stopped30");
    add(details, "stopped_60_count", "stopped60");
    add(details, "missed_count", "missedTraining");
    add(details, "feedback_pending_count", "feedback");
    add(details, "planned_count", "pending");
    const outcomeFields = ["full_count", "stopped_30_count", "stopped_60_count"];
    if (outcomeFields.every((field) => count(data, field) !== null) &&
        count(data, "completed_count") > outcomeFields.reduce((sum, field) => sum + data[field], 0) &&
        count(data, "feedback_pending_count") === 0) details.push(copy.unconfirmedTraining);
    if (positive(data, "feedback_pending_count")) tone = "attention";
    if (TRAINING_SIGNALS.slice(0, 4).some((field) => positive(data, field))) tone = "negative";
  } else {
    const supplements = key === "supplements";
    const missedKey = supplements ? "missed_window_count" : "missed_count";
    const openKey = supplements ? "open_window_count" : "open_count";
    const assumedKey = supplements ? "assumed_window_count" : "assumed_completed_count";
    completedKey = supplements ? "taken_window_count" : key === "meals" ? "explicit_completed_count" : "completed_count";
    fields = [completedKey, missedKey, openKey, ...(key === "recovery" ? [] : [assumedKey])];
    add(summary, completedKey, supplements ? "taken" : key === "meals" ? "confirmed" : "completed");
    add(details, missedKey, key === "meals" ? "missedMeal" : "missed");
    add(details, openKey, "pending");
    if (key !== "recovery") add(context, assumedKey, "assumed");
    if (positive(data, missedKey)) tone = "negative";
    if (supplements) {
      item = preferredItem(data?.windows);
      const time = total === 1 ? windowTime(item, timezone) : "";
      if (time) summary.push(time);
    }
  }

  const completeStatus = fields.every((field) => count(data, field) !== null);
  if (total !== 0 && data && !completeStatus) context.push(copy.statusUnavailable);
  if (tone === "neutral" && total > 0 && completeStatus && count(data, completedKey) === total &&
      fields.filter((field) => field !== completedKey && !(key === "training" && field === "completed_count"))
        .every((field) => count(data, field) === 0)) {
    tone = "positive";
  }

  let target = { tab: key === "meals" ? "nutrition" : "calendar", ...(date ? { date } : {}) };
  if (key === "training" && item?.injury_id) {
    target = { tab: "recovery", injuryId: item.injury_id, ...(date ? { date } : {}) };
  } else {
    // Mixed windows retain cancelled dose IDs without per-dose statuses.
    // Only a single-ID, non-cancelled window identifies a safe item to focus.
    const windowIds = list(item?.schedule_item_ids);
    const scheduleItemId = key === "supplements"
      ? (windowIds.length === 1 ? windowIds[0] : undefined) : item?.schedule_item_id;
    if (scheduleItemId) target = { ...target, tab: "calendar", scheduleItemId };
  }
  return { ...category, summary: summary.join(" · "), detail: details.join(" · "), context: context.join(" · "), tone, target };
}

export default function CareCockpitToday({ today, timezone, onOpen }) {
  const { lang = "de" } = useI18n();
  const copy = COPY[lang] || COPY.en;
  const headingId = useId();
  // The projection date is a player-local civil date, not a UTC timestamp.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(today?.date || "") &&
    dayjs(today.date).isValid() && dayjs(today.date).format("YYYY-MM-DD") === today.date ? today.date : null;
  const empty = CATEGORIES.every(({ key, totalKey }) => count(today?.[key], totalKey) === 0) &&
    !TRAINING_SIGNALS.some((field) => positive(today?.training, field));

  return <section className="care-cockpit-today" aria-labelledby={headingId} data-testid="care-cockpit-today">
    <div className="care-cockpit-today-header">
      <h3 id={headingId} className="care-cockpit-today-title">{copy.today}</h3>
      {date && <time className="care-cockpit-today-date" dateTime={date}>
        {dayjs(date).locale(lang).format(lang === "de" ? "DD. MMM" : "D MMM")}
      </time>}
    </div>
    {!today || empty ? <p className="care-cockpit-today-empty" data-testid="care-cockpit-today-empty">
      {empty ? copy.empty : copy.unavailable}
    </p> : <div className="care-cockpit-today-rows">
      {CATEGORIES.map((category) => {
        const { key, Icon, summary, detail, context, tone, target } = rowFor(category, today[category.key], copy, date, timezone);
        return <button key={key} type="button" className={`care-cockpit-row care-cockpit-row--${key}`}
          data-tone={tone} data-testid={`care-cockpit-today-${key}`}
          aria-label={`${copy.open(copy[key])}: ${summary}${detail ? `. ${detail}` : ""}${context ? `. ${context}` : ""}`}
          disabled={typeof onOpen !== "function"} onClick={() => onOpen?.(target)}>
          <span className="care-cockpit-row__icon"><Icon size={19} aria-hidden="true" focusable="false" /></span>
          <span className="care-cockpit-row__body">
            <span className="care-cockpit-row__label">{copy[key]}</span>
            <span className="care-cockpit-row__summary">{summary}</span>
            {(detail || context) && <span className="care-cockpit-row__meta">
              {detail && <span className="care-cockpit-row__detail">{detail}</span>}
              {detail && context && <span className="care-cockpit-row__context" aria-hidden="true"> · </span>}
              {context && <span className="care-cockpit-row__context">{context}</span>}
            </span>}
          </span>
          <ChevronRight className="care-cockpit-row__chevron" size={17} aria-hidden="true" focusable="false" />
        </button>;
      })}
    </div>}
  </section>;
}
